'use client'

import { usePathname } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'

import type { Page } from '@/payload-types'

import { PageSidebarNav } from './PageSidebarNav'
import { SectionContent } from './SectionContent'

type PageContentClientProps = {
  page: Page
}

const INTERNAL_SCROLL_PATHS = ['/privacy-policy', '/terms-and-conditions']

export function PageContentClient({ page }: PageContentClientProps) {
  const pathname = usePathname()
  const useInternalScroll =
    INTERNAL_SCROLL_PATHS.some((path) => pathname?.includes(path)) ?? false
  const sections = page.sections ?? []

  const [filteredSectionId, setFilteredSectionId] = useState<string | null>(null)
  const [contentMetrics, setContentMetrics] = useState({
    height: 0,
    top: 0,
  })
  const contentRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const element = useInternalScroll ? scrollContainerRef.current : contentRef.current
    if (!element) return

    const updateMetrics = () => {
      const rect = element.getBoundingClientRect()
      setContentMetrics({
        height: element.offsetHeight,
        top: rect.top + window.scrollY,
      })
    }
    updateMetrics()

    const resizeObserver = new ResizeObserver(updateMetrics)
    resizeObserver.observe(element)

    window.addEventListener('resize', updateMetrics)

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', updateMetrics)
    }
  }, [useInternalScroll])

  const scrollToSection = (sectionId: string) => {
    const el = document.getElementById(sectionId)
    if (!el) return

    if (useInternalScroll) {
      const container = scrollContainerRef.current
      if (!container) return

      const containerRect = container.getBoundingClientRect()
      const elRect = el.getBoundingClientRect()
      const scrollTarget = container.scrollTop + (elRect.top - containerRect.top) - 8
      container.scrollTo({ top: Math.max(0, scrollTarget), behavior: 'smooth' })
      return
    }

    el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const handleSectionClick = (sectionId: string) => {
    if (!useInternalScroll) {
      setFilteredSectionId(sectionId)
    }
    scrollToSection(sectionId)
  }

  useEffect(() => {
    if (useInternalScroll || !filteredSectionId) return

    requestAnimationFrame(() => requestAnimationFrame(() => scrollToSection(filteredSectionId)))
  }, [filteredSectionId, useInternalScroll])

  const sectionList =
    sections.length > 0 ? (
      <div className="space-y-10">
        {sections.map((section, i) => (
          <div
            key={section.sectionId}
            id={section.sectionId}
            className={i > 0 ? 'border-t border-neutral-100 pt-10' : ''}
          >
            <SectionContent section={section} />
          </div>
        ))}
      </div>
    ) : (
      <p className="font-poppins text-neutral-600">No content yet.</p>
    )

  return (
    <div className="mx-auto flex max-w-[1280px] gap-8 lg:gap-12">
      {sections.length > 0 ? (
        <PageSidebarNav
          sections={sections}
          filteredSectionId={filteredSectionId}
          onSectionClick={handleSectionClick}
          contentMetrics={contentMetrics}
          scrollContainerRef={useInternalScroll ? scrollContainerRef : undefined}
        />
      ) : null}

      <main
        ref={contentRef}
        className={`min-w-0 flex-1 self-start ${useInternalScroll ? 'sticky top-8' : ''}`}
      >
        {useInternalScroll ? (
          <div
            ref={scrollContainerRef}
            className="max-h-[calc(100vh-4rem)] overflow-y-auto rounded-lg border border-neutral-200 bg-white p-8 shadow-sm"
          >
            {sectionList}
          </div>
        ) : (
          <div className="rounded-lg border border-neutral-200 bg-white p-8 shadow-sm">
            {sectionList}
          </div>
        )}
      </main>
    </div>
  )
}
